import {
  Box,
  Button,
  ButtonProps,
  Popover,
  PopoverContent,
  PopoverContentProps,
  PopoverTrigger,
  Tooltip,
  useBoolean,
} from '@chakra-ui/react';
import { ReactComponent as ChevronDown } from 'assets/icons/chevron_down.svg';
import { TOOLBAR_COMPONENT_HEIGHT } from 'constants/editor';
import { Suspense, lazy, memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { stopPropagation } from 'utils/event';

import { useDocumentFonts } from './DocumentFonts';
import { getMenuDisplayName } from './FontFamilySelect.helpers';

// Font images in the dropdown are kept as base64 strings in memory
// -> code split to keep separate from the main bundle
const FontFamilySelectMenu = lazy(
  () =>
    import(
      /* webpackChunkName: "FontFamilySelectMenu" */
      /* webpackPrefetch: true */
      './FontFamilySelectMenu'
    ),
);

type FontFamilySelectProps = {
  contentProps?: PopoverContentProps;
  fontFamily: string;
  onChange: (fontFamily: string) => void;
  onOpen?: () => void;
  onClose?: () => void;
  borderRadius?: string | number;
  width?: string;
  buttonProps?: ButtonProps;
  // style of the menu trigger button
  type?: 'toolbar' | 'default';
  // if true, the focus will return focus to the menu trigger button when closed
  hasReturnFocus?: boolean;
  // if true the dropdown will display "Multiple Fonts"
  isMultipleFont?: boolean;
};

const triggerButtonTypeStyle = {
  toolbar: {
    size: 'xs',
    borderColor: 'gray.500',
    fontWeight: 'medium',
    fontSize: 'xs',
    height: TOOLBAR_COMPONENT_HEIGHT,
  },
  default: {},
};

// we don't need the scroll event listener on the popover - it's a bit slow with a lot of fonts
const popoverModifiers = [{ name: 'eventListeners', options: { scroll: false } }];

export const FontFamilySelect = memo(
  ({
    onOpen,
    onClose,
    onChange,
    fontFamily,
    borderRadius = 'base',
    width = '6rem',
    type = 'default',
    buttonProps,
    hasReturnFocus = true,
    isMultipleFont = false,
    contentProps,
  }: FontFamilySelectProps) => {
    const [shouldReturnFocus, setShouldReturnFocus] = useBoolean(true);
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [isHover, setIsHover] = useBoolean();

    // If it is custom font, display the file name
    const { customFontsMap } = useDocumentFonts();
    const displayCustomFontFileName = customFontsMap?.get(fontFamily)?.file_name;
    const displayName = getMenuDisplayName({ isMultipleFont, fontFamily, displayCustomFontFileName });

    const { t } = useTranslation('editor_toolbar');
    const heading = t('fontFamily.heading');
    const documentFontHeading = t('fontFamily.documentFontHeading');
    const noMatchingFonts = t('fontFamily.noMatchingFonts');
    const label = t('fontFamily.label');
    const testId = 'font-family-dropdown-trigger';

    const onSetIsPopoverOpen = (isOpen: boolean) => {
      if (isOpen && isPopoverOpen) return;
      if (!isOpen && !isPopoverOpen) return;

      setIsPopoverOpen(isOpen);

      if (isOpen) onOpen?.();
      else onClose?.();
    };

    const onSetFont = (fontFamily: string) => {
      setShouldReturnFocus.off();
      onChange(fontFamily);
      onSetIsPopoverOpen(false);
    };

    const onTriggerClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      setShouldReturnFocus.on();
      onSetIsPopoverOpen(!isPopoverOpen);
    };

    const closePopover = () => onSetIsPopoverOpen(false);

    const onKeydown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (isPopoverOpen) stopPropagation(e);
    };

    const returnFocusOnClose = typeof hasReturnFocus === 'boolean' ? hasReturnFocus : shouldReturnFocus;
    const tooltipLabel = t('fontFamily.tooltip');

    return (
      <Tooltip isOpen={!isPopoverOpen && isHover} hasArrow placement='bottom' label={tooltipLabel} bg='black'>
        <Box onKeyDown={onKeydown}>
          <Popover
            isOpen={isPopoverOpen}
            onClose={closePopover}
            modifiers={popoverModifiers}
            returnFocusOnClose={returnFocusOnClose}
            placement='bottom-start'
            isLazy
          >
            <PopoverTrigger>
              <Button
                onMouseEnter={setIsHover.on}
                onMouseLeave={setIsHover.off}
                onClick={onTriggerClick}
                data-testid={testId}
                aria-label={tooltipLabel}
                rightIcon={<ChevronDown />}
                variant='ghost'
                size='sm'
                w={width}
                justifyContent='space-between'
                border='1px solid'
                borderColor='outline.gray'
                borderRadius={borderRadius}
                fontWeight='normal'
                _hover={{ bg: 'none' }}
                {...triggerButtonTypeStyle[type]}
                {...buttonProps}
              >
                <Box fontSize='sm' as='span' textOverflow='ellipsis' overflow='hidden' whiteSpace='nowrap'>
                  {displayName}
                </Box>
              </Button>
            </PopoverTrigger>
            <Box zIndex='fontMenu'>
              <PopoverContent width={60} height={'33.5rem'} border={0} boxShadow={'xs'} {...contentProps}>
                <Suspense fallback={<Box w='full' h={contentProps?.h ?? contentProps?.height ?? '33.5rem'} />}>
                  <FontFamilySelectMenu
                    heading={heading}
                    documentFontHeading={documentFontHeading}
                    label={label}
                    onClose={closePopover}
                    onChange={onSetFont}
                    fontFamily={fontFamily}
                    noMatchingFonts={noMatchingFonts}
                    isMultipleFont={isMultipleFont}
                  />
                </Suspense>
              </PopoverContent>
            </Box>
          </Popover>
        </Box>
      </Tooltip>
    );
  },
);
FontFamilySelect.displayName = 'FontFamilySelect';
